import { AlertTriangle, Info } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Chip } from "@/components/common/StatusBadge";
import { fmtMoney, fmtNumber } from "@/lib/format";
import { ESTIMATE_KIND_LABELS, PRICE_SOURCE_LABELS, unitShort } from "@/lib/i18n";
import { cn } from "@/lib/utils";

const SOURCE_TONE = { kundeavtale: "info", katalog: "neutral", standard: "neutral", manuell: "warning" };

const Row = ({ label, value, strong = false, muted = false, testId }) => (
  <div className={cn("flex items-baseline justify-between gap-3 text-sm", muted && "text-muted-foreground")} data-testid={testId}>
    <span>{label}</span>
    <span className={cn("num", strong && "text-base font-semibold text-foreground")}>{value}</span>
  </div>
);

/**
 * Viser ein kalkyle (overslag): linjer, føresetnader og summar. Berre for administrasjon.
 * compact=true gir ei tettare utgåve til sidepanel.
 */
export const EstimateSummary = ({ estimate, loading = false, error = null, emptyText = "Legg inn mengd, bil og rute for å få eit prisoverslag.", compact = false, testId = "estimate-summary" }) => {
  if (loading && !estimate) {
    return (
      <div className="space-y-3" data-testid={`${testId}-loading`} aria-busy="true">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }
  if (error) {
    return (
      <p className="flex items-start gap-2 text-sm text-destructive" data-testid={`${testId}-error`}>
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
      </p>
    );
  }
  if (!estimate) {
    return <p className="text-sm leading-6 text-muted-foreground" data-testid={`${testId}-empty`}>{emptyText}</p>;
  }

  const billable = estimate.lines.filter((l) => l.billable);
  const internal = estimate.lines.filter((l) => !l.billable);
  const facts = [
    estimate.loads ? `${estimate.loads} lass` : null,
    estimate.tonnes ? `${fmtNumber(estimate.tonnes, 1)} t` : null,
    estimate.total_hours ? `${fmtNumber(estimate.total_hours, 2)} t transport` : null,
  ].filter(Boolean);

  return (
    <div className={cn("space-y-4", loading && "opacity-70 transition-opacity")} data-testid={testId} aria-busy={loading}>
      <div>
        <p className="text-xs text-muted-foreground">{estimate.pricing_mode === "fixed" ? "Avtalt fastpris eks. mva" : "Overslag eks. mva"}</p>
        <p className="num mt-0.5 text-[26px] font-semibold leading-none tracking-tight" data-testid={`${testId}-subtotal`}>{fmtMoney(estimate.subtotal_excl_vat)}</p>
        {facts.length ? <p className="tabular mt-2 text-xs text-muted-foreground" data-testid={`${testId}-facts`}>{facts.join(" · ")}</p> : null}
      </div>

      <ul className="divide-y divide-border border-y border-border" data-testid={`${testId}-lines`}>
        {billable.length === 0 ? <li className="py-2 text-sm text-muted-foreground">Ingen fakturerbare linjer enno.</li> : null}
        {billable.map((l) => (
          <li key={l.id} className="py-2.5" data-testid={`${testId}-line`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium leading-5">{l.description}</p>
                <p className="tabular mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span>{ESTIMATE_KIND_LABELS[l.kind] || l.kind}</span>
                  {l.quantity !== null && l.quantity !== undefined ? <span>{fmtNumber(l.quantity, 2)} {unitShort(l.unit)}{l.unit_price !== null && l.unit_price !== undefined ? ` × ${fmtNumber(l.unit_price, 2)}` : ""}</span> : null}
                  {!compact && l.source ? <Chip tone={SOURCE_TONE[l.source] || "neutral"} outline>{PRICE_SOURCE_LABELS[l.source] || l.source}</Chip> : null}
                </p>
                {l.note && !compact ? <p className="mt-0.5 text-xs text-muted-foreground">{l.note}</p> : null}
              </div>
              <span className={cn("num shrink-0 text-sm", l.amount === null && "text-destructive")} data-testid={`${testId}-line-amount`}>{l.amount === null || l.amount === undefined ? "Pris manglar" : fmtMoney(l.amount)}</span>
            </div>
          </li>
        ))}
      </ul>

      <div className="space-y-1.5">
        {estimate.min_price_applied ? <Row label="Minstepris lagt til grunn" value="" muted /> : null}
        <Row label="Sum eks. mva" value={fmtMoney(estimate.subtotal_excl_vat)} />
        <Row label="Mva 25 %" value={fmtMoney(estimate.vat_amount)} muted />
        <Row label="Sum inkl. mva" value={fmtMoney(estimate.total_incl_vat)} strong testId={`${testId}-total`} />
        {internal.length ? <Row label="Intern kostnad (ikkje fakturert)" value={fmtMoney(estimate.internal_cost_excl_vat)} muted testId={`${testId}-internal`} /> : null}
      </div>

      {estimate.assumptions?.length ? (
        <div className="rounded-lg bg-background px-3 py-2.5" data-testid={`${testId}-assumptions`}>
          <p className="flex items-center gap-1.5 text-xs font-semibold"><Info className="h-3.5 w-3.5 text-muted-foreground" /> Føresetnader</p>
          <ul className="mt-1.5 space-y-1 text-xs leading-5 text-muted-foreground">
            {estimate.assumptions.map((a, i) => (
              <li key={i} className="flex gap-2"><span aria-hidden="true">–</span><span>{a}</span></li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
};
